/**
 * Hook Configuration Registry
 *
 * This module provides a scalable registry-based approach for managing different hook
 * implementations. Instead of using version enums, we use hook addresses as the primary
 * identifier, allowing for multiple hook types and variants at any "version level".
 *
 * Benefits:
 * - Supports unlimited hook types (not just linear versioning)
 * - Address is already in PoolKey, no additional data needed
 * - Easy to add new hooks without code changes
 * - Can include feature flags and metadata per hook
 */

import {
  verifiedPoolsBasicHookAddress,
  verifiedPoolsBasicHookV2Address,
  verifiedPoolsPositionManagerAddress,
  verifiedPoolsPositionManagerV2Address,
} from '@generated/generated';
import type { Address } from 'viem';

/**
 * Configuration for a specific hook implementation
 */
export type HookConfig = {
  /** Display name for the hook */
  name: string;
  /** Version label (for display/logging purposes) */
  version: string;
  /** Position manager address for this hook */
  positionManagerAddress: Address;
  /** Optional feature flags */
  features?: string[];
};

/**
 * Registry of all known hooks by chain ID and hook address
 *
 * Structure: { [chainId]: { [hookAddress]: HookConfig } }
 */
const HOOK_REGISTRY: Record<number, Record<string, HookConfig>> = {
  // Base Sepolia
  84532: {
    [verifiedPoolsBasicHookAddress[84532].toLowerCase()]: {
      name: 'Verified Pools Basic Hook',
      version: 'v1',
      positionManagerAddress: verifiedPoolsPositionManagerAddress[84532],
    },
    [verifiedPoolsBasicHookV2Address[84532].toLowerCase()]: {
      name: 'Verified Pools Basic Hook V2',
      version: 'v2',
      positionManagerAddress: verifiedPoolsPositionManagerV2Address[84532],
      features: ['dynamic-fees'],
    },
  },
  // Base Mainnet
  8453: {
    [verifiedPoolsBasicHookAddress[8453].toLowerCase()]: {
      name: 'Verified Pools Basic Hook',
      version: 'v1',
      positionManagerAddress: verifiedPoolsPositionManagerAddress[8453],
    },
  },
};

/**
 * Get configuration for a specific hook
 *
 * @param hookAddress - The hook contract address
 * @param chainId - The chain ID
 * @returns Hook configuration if found, undefined otherwise
 */
export function getHookConfig(hookAddress: Address, chainId: number): HookConfig | undefined {
  const chainRegistry = HOOK_REGISTRY[chainId];
  if (!chainRegistry) {
    return undefined;
  }
  return chainRegistry[hookAddress.toLowerCase()];
}

/**
 * Get the position manager address for a given hook
 *
 * @param hookAddress - The hook contract address
 * @param chainId - The chain ID
 * @returns The position manager address for this hook
 * @throws Error if hook is not found in registry
 */
export function getPositionManagerForHook(hookAddress: Address, chainId: number): Address {
  const config = getHookConfig(hookAddress, chainId);

  if (!config) {
    throw new Error(
      `Unknown hook: ${hookAddress} on chain ${chainId}. Please update the hook registry.`,
    );
  }

  return config.positionManagerAddress;
}

/**
 * Get all position manager addresses for a chain (for querying all positions)
 * Returns addresses in priority order (newer hooks first)
 *
 * @param chainId - The chain ID
 * @returns Array of position manager addresses in priority order (V2 first, then V1)
 */
export function getAllPositionManagerAddresses(chainId: number): Address[] {
  // Explicitly return position managers in priority order
  // This ensures we check newer versions first when querying positions
  switch (chainId) {
    case 84532: // Base Sepolia
      return [
        verifiedPoolsPositionManagerV2Address[84532],
        verifiedPoolsPositionManagerAddress[84532],
      ];
    case 8453: // Base Mainnet
      return [verifiedPoolsPositionManagerAddress[8453]];
    default:
      return [];
  }
}

/**
 * Check if a hook has a specific feature
 *
 * @param hookAddress - The hook contract address
 * @param chainId - The chain ID
 * @param feature - The feature to check for
 * @returns True if the hook has the feature, false otherwise
 */
export function hookHasFeature(hookAddress: Address, chainId: number, feature: string): boolean {
  const config = getHookConfig(hookAddress, chainId);
  return config?.features?.includes(feature) ?? false;
}

/**
 * Get the version label for a hook (for display purposes)
 *
 * @param hookAddress - The hook contract address
 * @param chainId - The chain ID
 * @returns Version label or 'unknown' if hook not found
 */
export function getHookVersion(hookAddress: Address, chainId: number): string {
  const config = getHookConfig(hookAddress, chainId);
  return config?.version ?? 'unknown';
}
